import React from "react";
import { Breadcrumb, Button, Space, Typography } from "antd";
import {
    CalendarOutlined,
    MoreOutlined,
    NumberOutlined,
    ShareAltOutlined,
    StarOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { WbSubject } from "../../workbench/types/WbTypes";
import WorkbenchNewEntityTypeTag from "./WorkbenchNewEntityTypeTag";

const { Title } = Typography;

const notAvailableLabel = "N/A";

type Props = {
    cedent: string;
    identifier: string;
    onFollow: (event: React.MouseEvent<HTMLElement>) => void;
    onMore: (event: React.MouseEvent<HTMLElement>) => void;
    onShare: () => void;
    subject?: WbSubject | null;
    title: string;
    uwYear: number | string;
};

const getHeaderValue = (value?: string | number | null) => {
    if (typeof value === "number") {
        return value;
    }

    if (typeof value === "string" && value.trim()) {
        return value;
    }

    return notAvailableLabel;
};

const getHeaderText = (value?: string | null) => {
    if (typeof value === "string" && value.trim()) {
        return value;
    }

    return notAvailableLabel;
};

const getContractReference = (subject?: WbSubject | null) => {
    if (subject?.kind !== "Contract") {
        return undefined;
    }

    const references = [
        subject.context?.customProperties?.umr,
        subject.context?.customProperties?.contractReference,
        subject.businessEntities?.find(
            (reference) =>
                reference.kind === "Contract" &&
                Boolean(reference.identifier?.trim()),
        )?.identifier,
    ];

    return references
        .find(
            (reference): reference is string =>
                typeof reference === "string" && Boolean(reference.trim()),
        )
        ?.trim();
};

const WorkbenchNewSubjectHeader: React.FC<Props> = ({
    cedent,
    identifier,
    onFollow,
    onMore,
    onShare,
    subject,
    title,
    uwYear,
}) => {
    const cedentDisplay = getHeaderText(cedent);
    const entityKindDisplay = getHeaderText(subject?.kind);
    const identifierDisplay = getHeaderText(identifier);
    const referenceDisplay = getHeaderText(getContractReference(subject));
    const titleDisplay = getHeaderText(title);
    const uwYearDisplay = getHeaderValue(uwYear);

    return (
        <div className="workbench-new-subject-header">
            <div>
                <Breadcrumb
                    items={[
                        {
                            title: (
                                <Link to="/workbench-new">Search results</Link>
                            ),
                        },
                        { title: identifierDisplay },
                    ]}
                />
                <div className="workbench-new-subject-title-row">
                    <Title level={1}>{titleDisplay}</Title>
                    <WorkbenchNewEntityTypeTag kind={entityKindDisplay} />
                </div>
                <Space
                    className="workbench-new-subject-meta"
                    wrap={true}
                    size={[18, 8]}
                >
                    <span>
                        <span
                            aria-hidden="true"
                            className="workbench-new-subject-company-icon"
                        />
                        {cedentDisplay}
                    </span>
                    <span>
                        <CalendarOutlined />
                        UW Year {uwYearDisplay}
                    </span>
                    <span>
                        <NumberOutlined />
                        ID: {referenceDisplay}
                    </span>
                </Space>
            </div>
            <Space className="workbench-new-subject-actions">
                <Button icon={<ShareAltOutlined />} onClick={onShare}>
                    Share
                </Button>
                <Button icon={<StarOutlined />} onClick={onFollow}>
                    Follow
                </Button>
                <Button
                    aria-label="More actions"
                    icon={<MoreOutlined />}
                    onClick={onMore}
                />
            </Space>
        </div>
    );
};

export default WorkbenchNewSubjectHeader;
